<template>
  <section v-if="hasContent" class="generic-report-view">
    <AskReportChips :report-view="reportView" />

    <section v-if="mainTakeaway" class="mode-card">
      <p class="snapshot-card__label">Main takeaway</p>
      <p class="answer-text" v-html="linkifyTicketRefs(mainTakeaway)" @click="onItemClick" />
    </section>

    <section v-if="whatsHappening" class="mode-card">
      <p class="snapshot-card__label">What&apos;s happening</p>
      <p class="answer-text" v-html="linkifyTicketRefs(whatsHappening)" @click="onItemClick" />
    </section>

    <section v-if="whatDataSuggests || whatToVerify" class="mode-card">
      <p class="snapshot-card__label">Why it may be happening</p>
      <div v-if="whatDataSuggests" class="report-card__subsection">
        <p class="report-card__subheading">What the data suggests</p>
        <p class="answer-text" v-html="linkifyTicketRefs(whatDataSuggests)" @click="onItemClick" />
      </div>
      <div v-if="whatToVerify" class="report-card__subsection">
        <p class="report-card__subheading">What to verify</p>
        <p class="answer-text" v-html="linkifyTicketRefs(whatToVerify)" @click="onItemClick" />
      </div>
    </section>

    <section v-if="recommendedActions.length" class="mode-card">
      <p class="snapshot-card__label">Recommended actions</p>
      <ol class="snapshot-list snapshot-list--numbered">
        <li
          v-for="(item, index) in recommendedActions"
          :key="`report-action-${index}`"
          v-html="linkifyTicketRefs(item)"
          @click="onItemClick"
        />
      </ol>
    </section>

    <section v-if="patterns.length" class="mode-card">
      <p class="snapshot-card__label">Patterns</p>
      <ul class="snapshot-list snapshot-list--bulleted">
        <li
          v-for="(item, index) in patterns"
          :key="`report-pattern-${index}`"
          v-html="linkifyTicketRefs(item)"
          @click="onItemClick"
        />
      </ul>
    </section>

    <section v-if="!hideDataContext && (dataContextItems.length || limitations.length || demoNote)" class="mode-card">
      <p class="snapshot-card__label">Data context and limits</p>
      <dl v-if="dataContextItems.length" class="dataset-context">
        <div v-for="item in dataContextItems" :key="item.label" class="dataset-context__item">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </div>
      </dl>
      <div v-if="limitations.length" class="report-card__limitations">
        <p class="report-card__subheading">Limits</p>
        <ul class="snapshot-list snapshot-list--bulleted">
          <li v-for="(limitation, idx) in limitations" :key="`report-limitation-${idx}`">
            {{ limitation }}
          </li>
        </ul>
      </div>
      <div v-if="demoNote" class="report-card__demo-note">
        <p class="report-card__subheading">Demo data</p>
        <p class="answer-text">{{ demoNote }}</p>
      </div>
    </section>
  </section>
</template>

<script>
import { ticketRefClass } from '@/utils/visitedTickets'
import AskReportChips from '@/components/roster/ask/AskReportChips.vue'

export default {
  name: 'AskReportView',
  components: {
    AskReportChips,
  },
  props: {
    reportView: {
      type: Object,
      default: () => null,
    },
    signalStrength: {
      type: Object,
      default: () => ({}),
    },
    hideDataContext: {
      type: Boolean,
      default: false,
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  computed: {
    mainTakeaway() {
      return String(this.reportView?.mainTakeaway || '').trim()
    },
    whatsHappening() {
      return String(this.reportView?.whatsHappening || '').trim()
    },
    whatDataSuggests() {
      return String(this.reportView?.whyItMayBeHappening?.whatDataSuggests || '').trim()
    },
    whatToVerify() {
      return String(this.reportView?.whyItMayBeHappening?.whatToVerify || '').trim()
    },
    recommendedActions() {
      return Array.isArray(this.reportView?.recommendedActions) ? this.reportView.recommendedActions : []
    },
    patterns() {
      return Array.isArray(this.reportView?.patterns) ? this.reportView.patterns : []
    },
    dataContextItems() {
      return Array.isArray(this.reportView?.dataContextAndLimits?.items)
        ? this.reportView.dataContextAndLimits.items
            .map((item) => ({
              label: String(item?.label || '').trim(),
              value: String(item?.value || '').trim(),
            }))
            .filter((item) => item.label && item.value)
        : []
    },
    limitations() {
      return Array.isArray(this.reportView?.dataContextAndLimits?.limitations)
        ? this.reportView.dataContextAndLimits.limitations
        : []
    },
    demoNote() {
      const note = this.reportView?.dataContextAndLimits?.demoNote
      return typeof note === 'string' ? note.trim() : ''
    },
    hasContent() {
      return Boolean(
        this.reportView?.signalAndActionSummary?.signalStrengthLabel ||
        this.reportView?.signalAndActionSummary?.actionStatusLabel ||
        this.mainTakeaway ||
        this.whatsHappening ||
        this.whatDataSuggests ||
        this.whatToVerify ||
        this.recommendedActions.length ||
        this.patterns.length ||
        this.dataContextItems.length ||
        this.limitations.length ||
        this.demoNote,
      )
    },
  },
  methods: {
    linkifyTicketRefs(text) {
      if (!text || !this.ticketRefs.length) return this.escapeHtml(text)
      const ticketNbrs = this.ticketRefs.map((t) => t.ticketNbr).filter(Boolean)
      if (!ticketNbrs.length) return this.escapeHtml(text)
      const escaped = this.escapeHtml(text)
      const pattern = new RegExp(`\\b(${ticketNbrs.map(this.escapeRegex).join('|')})\\b`, 'g')
      return escaped.replace(pattern, (match) => {
        return `<span class="${ticketRefClass(match)}" data-ticket-nbr="${match}" role="button" tabindex="0">${match}</span>`
      })
    },
    escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    onItemClick(event) {
      const ticketNbr = event.target?.dataset?.ticketNbr
      if (ticketNbr) {
        this.$emit('ticket-ref-clicked', ticketNbr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.generic-report-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.mode-card {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.report-card__subsection,
.report-card__limitations,
.report-card__demo-note {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.snapshot-card__label,
.report-card__subheading,
.answer-text,
.dataset-context__item dd {
  margin: 0;
}

.snapshot-card__label {
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.report-card__subheading {
  font-size: $font-size-sm;
  font-weight: 600;
  color: $color-text-strong;
}

.answer-text {
  line-height: 1.6;
  color: $color-text-muted;
}

.snapshot-list {
  margin: 0;
  padding-left: 1.2rem;
}

.snapshot-list--bulleted {
  list-style-type: disc;
}

.snapshot-list--numbered {
  list-style-type: decimal;
}

.snapshot-list li + li {
  margin-top: $space-2;
}

.dataset-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: 0;
}

.dataset-context__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.dataset-context__item dt {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

@media (max-width: 900px) {
  .dataset-context {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .mode-card {
    padding: $space-3;
  }
}

:deep(.ticket-ref) {
  color: $color-primary-dark;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: $color-primary;
  }

  &.ticket-ref--visited {
    color: $color-text-muted;
  }
}
</style>
